import { Check, Copy, Package, Zap } from "lucide-react";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { CiDeployInfo } from "@/lib/types";

interface DeployInfoCardProps {
  deploy: CiDeployInfo | null | undefined;
}

interface Row {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}

function DeployRow({ row }: { row: Row }) {
  const [copied, setCopied] = useState(false);

  if (!row.value) {
    return (
      <div className="flex items-center justify-between gap-2 py-1.5 text-[13px]">
        <span className="text-muted-foreground">{row.label}</span>
        <span className="text-muted-foreground">—</span>
      </div>
    );
  }

  const handleCopy = async () => {
    if (!row.value) return;
    // navigator.clipboard is undefined on non-HTTPS origins (the dev URL is HTTP)
    // and rejects under restrictive permission policies — swallow both.
    try {
      await navigator.clipboard.writeText(row.value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1_200);
    } catch {
      // Copy unavailable; leave button in idle state.
    }
  };

  return (
    <div className="flex items-center justify-between gap-2 py-1.5 text-[13px]">
      <span className="text-muted-foreground shrink-0">{row.label}</span>
      <div className="flex min-w-0 items-center gap-1.5">
        <span className={row.mono ? "truncate font-mono text-[12px]" : "truncate"}>
          {row.value}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          aria-label={`Copy ${row.label}`}
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
        >
          {copied ? <Check className="size-3.5 text-emerald-500" /> : <Copy className="size-3.5" />}
        </button>
      </div>
    </div>
  );
}

function protocolTint(protocol: string | null | undefined): string {
  if (protocol === "rdma") return "text-primary";
  if (protocol === "tcp") return "text-amber-500";
  return "text-muted-foreground";
}

export function DeployInfoCard({ deploy }: DeployInfoCardProps) {
  if (!deploy) return null;

  const rows: Row[] = [
    { label: "cryod", value: deploy.cryod_image, mono: true },
    // The build commit pins a moving tag (latest-<user>) to one cryod build.
    // Copy it into the coverage matrix's Tag → commit pin to isolate this build.
    { label: "build", value: deploy.cryod_revision, mono: true },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          <Package className="size-4" />
          Deploy
          {deploy.mooncake_protocol && (
            <span
              className={`ml-auto inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider ${protocolTint(deploy.mooncake_protocol)}`}
            >
              <Zap className="size-3" />
              mooncake:{deploy.mooncake_protocol}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="divide-y divide-border py-0">
        {rows.map((row) => (
          <DeployRow key={row.label} row={row} />
        ))}
      </CardContent>
    </Card>
  );
}
